<template>
  <Modal
    sheetbelowsm
    :title="editData ? $t('projects.endpoints.update') : $t('projects.endpoints.create')"
    :primary-button="$t('save')"
    :secondary-button="$t('cancel')"
    :primary-button-disabled="loading"
    :primary-button-loading="loading"
    close-button
    :loading="fetching"
    @primary="onSaveClick"
    @secondary="onCancelClick"
    @close="onCancelClick"
  >
    <div class="w-full sm:w-800 px-10 text-semi-dark-text">
      <div class="text-13 mt-4">{{ $t('projects.endpoints.create_popup_description') }}</div>
      <div class="flex mt-4 mb-4 gap-x-4">
        <div
          class="border border-solid p-4 rounded-4 select-none"
          :class="{
            'border-primary': selectedType === 'api-request',
            'border-stroke': selectedType !== 'api-request',
            'cursor-pointer': !editDataType,
            'opacity-50 pointer-events-none': editDataType && editDataType !== 'api-request',
          }"
          @click="onTypeSelect('api-request')"
        >
          <div class="mb-2 flex items-center">
            <Icon name="settings_ethernet" class="w-6 h-6 mr-3 shrink-0" />
            <div class="text-14 font-500">{{ $t('projects.endpoints.api_request') }}</div>
          </div>
          <div class="text-12">{{ $t('projects.endpoints.api_request_description') }}</div>
        </div>
        <div
          class="border border-solid p-4 rounded-4 select-none cursor-pointer"
          :class="{
            'border-primary': selectedType === 'scheduled-task',
            'border-stroke': selectedType !== 'scheduled-task',
            'cursor-pointer': !editDataType,
            'opacity-50 pointer-events-none': editDataType && editDataType !== 'scheduled-task',
          }"
          @click="onTypeSelect('scheduled-task')"
        >
          <div class="mb-2 flex items-center">
            <Icon name="schedule" class="w-6 h-6 mr-3 shrink-0" />
            <div class="text-14 font-500">{{ $t('projects.endpoints.scheduled_task') }}</div>
          </div>
          <div class="text-12">{{ $t('projects.endpoints.scheduled_task_description') }}</div>
        </div>
        <div
          class="border border-solid p-4 rounded-4 select-none cursor-pointer"
          :class="{
            'border-primary': selectedType === 'queue-consumer',
            'border-stroke': selectedType !== 'queue-consumer',
            'cursor-pointer': !editDataType,
            'opacity-50 pointer-events-none': editDataType && editDataType !== 'queue-consumer',
          }"
          @click="onTypeSelect('queue-consumer')"
        >
          <div class="mb-2 flex items-center">
            <Icon name="flex_direction" class="w-6 h-6 mr-3 shrink-0" />
            <div class="text-14 font-500">{{ $t('projects.endpoints.queue_consumer') }}</div>
          </div>
          <div class="text-12">{{ $t('projects.endpoints.queue_consumer_description') }}</div>
        </div>
      </div>
      <Divider v-if="selectedType" />
      <div v-if="selectedType" class="mt-3 text-14 font-500">{{ $t('configuration') }}</div>
      <ValidationObserver ref="validationObserver" v-slot="{ errors }">
        <div v-if="selectedType === 'api-request'" class="w-full">
          <div class="w-full flex justify-start mt-1 gap-x-4">
            <FormSelect
              v-model="method"
              :options="methods"
              name="method"
              class="w-full"
              :error="errors.method"
              rules="required"
              :label="$t('projects.endpoints.method')"
              :reduce="(option) => option.value"
              :searchable="false"
              :clearable="false"
            ></FormSelect>
            <FormInput
              :readonly="loading"
              v-model="path"
              name="path"
              class="w-full"
              :error="errors.path"
              :rules="['required', { validate: pathValidation }]"
              :label="$t('projects.endpoints.path')"
            ></FormInput>
          </div>
          <div class="w-full flex justify-start py-2 gap-x-4">
            <FormInput :readonly="loading" v-model="groupName" name="groupName" class="w-full" :error="errors.groupName" rules="required" :label="$t('projects.endpoints.group_name')"></FormInput>
            <FormSelect
              :options="flows"
              append-to-body
              :readonly="loading"
              v-model="flowId"
              default-reducer
              name="flowId"
              class="w-full"
              :error="errors.flowId"
              rules="required"
              show-add-button
              :label="$t('projects.flows.flow')"
              @add="onAddFlowClick"
            ></FormSelect>
          </div>
          <div class="w-full py-2 gap-x-4">
            <div class="text-14 font-500">{{ $t('projects.authentication.title') }}</div>
            <div class="flex mt-1">
              <Checkbox v-model="authNone" class="mr-5 !text-13" label="None" />
              <Checkbox v-if="hasAuthFirebase" v-model="authFirebase" class="mr-5 !text-13" label="Firebase" />
              <Checkbox v-if="hasAuthApiKey" v-model="authApiKey" class="mr-5 !text-13" label="API Key" />
            </div>
          </div>
          <div class="py-4 w-full">
            <div class="text-14 font-500 mb-3">{{ $t('flow.request_parameters') }}</div>
            <table class="w-full max-sm:block">
              <thead class="max-sm:block">
                <tr class="max-sm:hidden">
                  <th class="border-b font-500 text-13 text-left" width="40%">{{ $t('flow.parameter') }}</th>
                  <th class="border-b font-500 text-13 text-left" width="40%">{{ $t('flow.assign_to') }}</th>
                  <th class="border-b font-500 text-13" width="100">{{ $t('flow.required') }}</th>
                  <th class="border-b font-500 text-13" width="50">{{ $t('flow.action') }}</th>
                </tr>
              </thead>
              <tbody class="max-sm:block">
                <tr v-if="!requestParams.length">
                  <td colspan="4" class="text-center text-dark-text pt-2 text-13">{{ $t('projects.endpoints.no_parameters_assigned') }}</td>
                </tr>
                <tr v-for="(requestParameter, index) in requestParams" :key="requestParameter.id" class="max-sm:flex max-sm:flex-col max-sm:w-full max-sm:pb-3">
                  <td class="max-sm:flex max-sm:items-center max-sm:mb-1 sm:h-10 sm:pt-4 sm:pr-5">
                    <label class="sm:hidden font-500 mr-3">{{ $t('flow.parameter') }}</label>
                    <Input v-model="requestParameter.key" class="w-full" />
                  </td>
                  <td class="max-sm:flex max-sm:items-center max-sm:mb-1 sm:h-10 sm:pt-4">
                    <label class="sm:hidden font-500 mr-3">{{ $t('flow.variable') }}</label>
                    <VariableSelect v-model="requestParameter.variable" system :filter="endpointAssignVariableSelectFilter" />
                  </td>
                  <td class="max-sm:flex max-sm:items-center max-sm:mb-1 sm:h-10 sm:pt-4">
                    <label class="sm:hidden font-500 mr-3">{{ $t('flow.required') }}</label>
                    <div class="max-sm:ml-auto max-sm:mr-1 flex justify-center items-center">
                      <Checkbox v-model="requestParameter.required" />
                    </div>
                  </td>
                  <td class="max-sm:flex max-sm:items-center max-sm:mb-1 sm:h-10 sm:pt-4">
                    <label class="sm:hidden font-500 mr-3">{{ $t('flow.action') }}</label>
                    <div class="max-sm:ml-auto flex justify-center items-center"><IconButton icon="delete" class="text-primary" @click="removeRequestParameter(index)" /></div>
                  </td>
                </tr>
              </tbody>
            </table>
            <div class="flex justify-end">
              <PillButton primary class="mt-4" :text="$t('flow.add_parameter')" @click="onAddRequestParameterClick"></PillButton>
            </div>
          </div>
        </div>
        <div v-else-if="selectedType === 'queue-consumer'" class="w-full">
          <FormSelect
            v-model="queueConsumerConfig.queue"
            :options="[
              { label: 'Queue 1', value: 'queue1' },
              { label: 'Queue 2', value: 'queue2' },
            ]"
            append-to-body
            :label="$t('projects.endpoints.select_queue')"
            class="mt-2"
          />
          <FormInput label="Message batch size" v-model="queueConsumerConfig.batchSize" type="number" class="mt-2" />
          <FormInput label="Message retries" v-model="queueConsumerConfig.retries" type="number" class="mt-2" />
        </div>
        <div v-else-if="selectedType === 'scheduled-task'" class="w-full">
          <FormSelect v-model="flowId" :options="flows" :label="$t('projects.flows.flow')" class="mt-2" name="flowid" :error="errors.flowid" rules="required" append-to-body default-reducer />

          <FormSelect
            v-model="frequency"
            :options="[
              { label: 'Minute', value: 'minute' },
              { label: 'Hour', value: 'hour' },
              { label: 'Day', value: 'day' },
              { label: 'Day of the week', value: 'weekday' },
              { label: 'Month', value: 'month' },
              { label: 'Bi-Monthly', value: 'bi-month' },
              { label: 'Quarterly', value: 'quarter' },
            ]"
            :label="$t('projects.endpoints.execute_worker_every')"
            class="mt-2"
            append-to-body
            default-reducer
          />

          <FormInput v-if="frequency === 'minute'" v-model="schedule" type="number" class="mt-2" />
          <FormInput v-if="frequency === 'hour'" v-model="schedule" type="number" class="mt-2" />
          <FormInput v-if="frequency === 'day'" v-model="schedule" type="number" class="mt-2" />
          <FormSelect v-if="frequency === 'weekday'" v-model="schedule" :options="weekDayList" append-to-body class="mt-2" default-reducer />
          <FormSelect v-if="frequency === 'month' || frequency === 'bi-month' || frequency === 'quarter'" v-model="schedule" :options="dayList" append-to-body default-reducer class="mt-2" />
          <FormTimeInput v-if="frequency === 'day' || frequency === 'weekday' || frequency === 'month' || frequency === 'bi-month' || frequency === 'quarter'" v-model="time" class="mt-2 w-100" />
        </div>
      </ValidationObserver>
    </div>
  </Modal>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import uuidv4 from '@/helpers/uuid';
import CreateProjectFlow from '@/components/project/CreateProjectFlow.vue';
import { AUTH_TYPES, WEEKDAYS } from '@/constants';

export default {
  name: 'CreateProjectEndpointModal',
  props: {
    projectId: {
      type: String,
      required: true,
    },
    editData: {
      type: Object,
    },
    editDataType: String,
    authProviders: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loading: false,
      fetching: false,
      name: '',
      nameError: '',
      method: '',
      methodError: '',
      methods: [
        { label: 'GET', value: 'GET' },
        { label: 'POST', value: 'POST' },
        { label: 'PUT', value: 'PUT' },
        { label: 'DELETE', value: 'DELETE' },
      ],
      authenticationTypes: [AUTH_TYPES.NONE],
      path: '',
      groupName: '',
      groupNameError: '',
      flowId: '',
      requestParams: [],
      selectedType: 'api-request',
      frequency: 'month',
      dayList: [...Array(30).keys()].map((i) => ({ label: i + 1, value: i + 1 })),
      weekDayList: WEEKDAYS,
      schedule: 1,
      hour: 0,
      minute: 0,
      queueConsumerList: [],
      queueConsumerConfig: {
        queue: '',
        batchSize: 10,
        retries: 3,
      },
    };
  },
  computed: {
    ...mapState(['projectData']),
    ...mapGetters(['getProjectAuthProviders', 'getProjectFlows']),
    flows() {
      return Object.keys(this.getProjectFlows()).map((flowId) => ({ label: this.getProjectFlows()[flowId].name, value: flowId }));
    },
    time: {
      get() {
        return `${this.hour}:${this.minute}`;
      },
      set(value) {
        this.hour = value.split(':')[0];
        this.minute = value.split(':')[1];
      },
    },
    hasAuthFirebase() {
      return this.authProviders.includes(AUTH_TYPES.FIREBASE);
    },
    hasAuthApiKey() {
      return this.authProviders.includes(AUTH_TYPES.API_KEY);
    },
    authNone: {
      get() {
        return this.authenticationTypes.includes(AUTH_TYPES.NONE);
      },
      set(value) {
        if (value) {
          if (!this.authenticationTypes.includes(AUTH_TYPES.NONE)) {
            this.authenticationTypes.push(AUTH_TYPES.NONE);
          }
          this.authenticationTypes = this.authenticationTypes.filter((i) => i !== AUTH_TYPES.FIREBASE && i !== AUTH_TYPES.API_KEY);
          this.authFirebase = false;
          this.authApiKey = false;
        } else {
          this.authenticationTypes = this.authenticationTypes.filter((i) => i !== AUTH_TYPES.NONE);
        }
      },
    },
    authFirebase: {
      get() {
        return this.authenticationTypes.includes(AUTH_TYPES.FIREBASE);
      },
      set(value) {
        if (value) {
          if (!this.authenticationTypes.includes(AUTH_TYPES.FIREBASE)) {
            this.authenticationTypes.push(AUTH_TYPES.FIREBASE);
          }
          this.authenticationTypes = this.authenticationTypes.filter((i) => i !== AUTH_TYPES.NONE);
          this.authNone = false;
        } else {
          this.authenticationTypes = this.authenticationTypes.filter((i) => i !== AUTH_TYPES.FIREBASE);
        }
      },
    },
    authApiKey: {
      get() {
        return this.authenticationTypes.includes(AUTH_TYPES.API_KEY);
      },
      set(value) {
        if (value) {
          if (!this.authenticationTypes.includes(AUTH_TYPES.API_KEY)) {
            this.authenticationTypes.push(AUTH_TYPES.API_KEY);
          }
          this.authenticationTypes = this.authenticationTypes.filter((i) => i !== AUTH_TYPES.NONE);
          this.authNone = false;
        } else {
          this.authenticationTypes = this.authenticationTypes.filter((i) => i !== AUTH_TYPES.API_KEY);
        }
      },
    },
  },
  methods: {
    ...mapActions([
      'createProjectEndpoint',
      'fetchProjectEndpoint',
      'updateProjectEndpoint',
      'createProjectScheduledTask',
      'updateProjectScheduledTask',
      'showToastMessage',
      'fetchProjectScheduledTask',
    ]),
    onTypeSelect(type) {
      this.selectedType = type;
    },
    onAddFlowClick() {
      this.$showModal(CreateProjectFlow);
    },
    endpointAssignVariableSelectFilter(variable) {
      return !variable.tags.includes('readonly') && !variable.tags.includes('templateonly');
    },
    async onSaveClick() {
      let response;
      if (this.selectedType === 'scheduled-task') {
        const valid = await this.$refs.validationObserver.validate();
        if (!valid.valid) return;
        const config = {
          project_id: this.projectId,
          frequency: this.frequency,
          schedule: this.schedule,
          hour: this.hour,
          minute: this.minute,
          flow_id: this.flowId,
        };
        if (this.frequency !== 'day' && this.frequency !== 'weekday' && this.frequency !== 'month' && this.frequency !== 'bi-month' && this.frequency !== 'quarter') {
          delete config.hour;
          delete config.minute;
        }
        this.loading = true;
        if (this.editData) {
          response = await this.updateProjectScheduledTask({ ...config, task_id: this.editData.task_id });
        } else {
          response = await this.createProjectScheduledTask(config);
        }
      } else if (this.selectedType === 'queue-consumer') {
        this.queueConsumerList.push(this.queueConsumerConfig);
        localStorage.setItem('queueConsumerList', JSON.stringify(this.queueConsumerList));
        this.$emit('save');
        this.$emit('close');
      } else {
        const valid = await this.$refs.validationObserver.validate();
        if (!valid.valid) return;
        this.loading = true;

        const requestParams = this.requestParams
          .filter((item) => item.key.trim() && item.variable.trim())
          .map((item) => ({
            key: item.key.trim(),
            variable: item.variable,
            required: item.required,
          }));

        if (!this.editData) {
          response = await this.createProjectEndpoint({
            project_id: this.projectId,
            method: this.method,
            path: this.path,
            group_name: this.groupName,
            flow_id: this.flowId,
            request_params: requestParams,
            authentication: this.authenticationTypes,
          });
        } else {
          response = await this.updateProjectEndpoint({
            endpoint_id: this.editData.endpoint_id,
            project_id: this.projectId,
            method: this.method,
            path: this.path,
            group_name: this.groupName,
            flow_id: this.flowId,
            request_params: requestParams,
            authentication: this.authenticationTypes,
          });
        }
      }
      if (response) {
        this.$emit('save');
        this.$emit('close');
      }
      this.loading = false;
    },
    onCancelClick() {
      this.$emit('close');
    },
    pathValidation() {
      return this.path.startsWith('/') || this.$t('projects.endpoints.path_starts_slashes');
    },
    onAddRequestParameterClick() {
      this.requestParams.push({
        id: uuidv4(),
        key: '',
        variable: '',
        required: false,
      });
    },
    removeRequestParameter(index) {
      this.requestParams.splice(index, 1);
    },
  },
  async created() {
    let queueConsumerList = [];
    try {
      queueConsumerList = JSON.parse(localStorage.getItem('queueConsumerList')) || [];
    } catch {
      queueConsumerList = [];
    }
    this.queueConsumerList = queueConsumerList;

    if (this.editData) {
      this.fetching = true;
      if (this.editDataType === 'api-request') {
        this.selectedType = this.editDataType;
        const response = await this.fetchProjectEndpoint({ projectId: this.projectId, endpointId: this.editData.endpoint_id });
        if (!response) return;
        const { method, path, group_name, flow_id, request_params, authentication } = response;
        this.method = method;
        this.path = path;
        this.groupName = group_name;
        this.flowId = flow_id;
        this.authenticationTypes = authentication || [];
        this.requestParams = request_params.map((item) => ({ id: uuidv4(), ...item }));
      } else if (this.editDataType === 'scheduled-task') {
        this.selectedType = this.editDataType;
        const response = await this.fetchProjectScheduledTask({ projectId: this.projectId, taskId: this.editData.task_id });
        if (!response) return;
        const { frequency, schedule, flow_id, hour, minute } = response;
        this.flowId = flow_id;
        this.frequency = frequency;
        this.schedule = schedule;
        this.hour = hour;
        this.minute = minute;
      }
      this.fetching = false;
    }
  },
};
</script>
